import { createStore } from "./store";
import { nameKey } from "../data/keys";
import type { Tab } from "./app";

export type Overlay = "filters" | "settings" | "nameoff" | "chosen" | { name: string } | null;

export interface Route {
  tab: Tab;
  overlay: Overlay;
  /** where in Explore: a section, then what's open in it (an origin, a letter, a collection) */
  path: string[];
}

const TABS: Tab[] = ["swipe", "list", "explore", "together"];
const OVERLAY_WORDS = ["name", "filters", "settings", "nameoff", "chosen"];

/**
 * Code from an invite link (#join=XXXXXXXX), kept until the user joins or backs out, for a week:
 * someone who opens the link and comes back later (the app closed meanwhile) still joins.
 */
const PENDING_KEY = "nameling:pending-join";
const PENDING_DAYS = 7;
let pendingJoinCode: string | null = (() => {
  try {
    const v = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "null") as { code?: unknown; at?: unknown } | null;
    return v && typeof v.code === "string" && typeof v.at === "number" && Date.now() - v.at < PENDING_DAYS * 86_400_000 ? v.code : null;
  } catch {
    return null;
  }
})();
export function peekPendingJoin(): string | null {
  return pendingJoinCode;
}
export function clearPendingJoin(): void {
  pendingJoinCode = null;
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* storage blocked: it was only in memory */
  }
}
/** A code typed in on the welcome screen works like an invite link. */
export function setPendingJoin(code: string): void {
  pendingJoinCode = code;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ code, at: Date.now() }));
  } catch {
    /* storage blocked: kept in memory */
  }
}

function decode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

function parse(hash: string): Route {
  const join = /^#join=([A-Za-z0-9-]{4,12})/.exec(hash);
  if (join) {
    setPendingJoin(join[1].toUpperCase().replace(/-/g, ""));
    history.replaceState(null, "", "#/together");
    return { tab: "together", overlay: null, path: [] };
  }
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const tab = (TABS.includes(parts[0] as Tab) ? parts[0] : "swipe") as Tab;
  const rest = parts.slice(1);
  const path: string[] = [];
  if (tab === "explore") while (rest.length && path.length < 2 && !OVERLAY_WORDS.includes(rest[0])) path.push(decode(rest.shift()!));
  let overlay: Overlay = null;
  // a link may carry any spelling of the name (#/explore/name/Olivia): look it up by its key
  if (rest[0] === "name" && rest[1]) overlay = { name: nameKey(decode(rest[1])) || decode(rest[1]) };
  else if (rest[0] === "filters" || rest[0] === "settings" || rest[0] === "nameoff" || rest[0] === "chosen") overlay = rest[0];
  return { tab, overlay, path };
}

function hashFor(r: Route): string {
  let h = `#/${r.tab}`;
  for (const p of r.path) h += `/${encodeURIComponent(p)}`;
  if (r.overlay && typeof r.overlay === "object") h += `/name/${encodeURIComponent(r.overlay.name)}`;
  else if (r.overlay) h += `/${r.overlay}`;
  return h;
}

export const route = createStore<Route>(parse(location.hash));

/** Explore remembers where you were while you're on other tabs */
let explorePath: string[] = route.get().tab === "explore" ? route.get().path : [];
route.subscribe(() => {
  const r = route.get();
  if (r.tab === "explore") explorePath = r.path;
});

/** how many overlay entries we pushed onto history in this session */
let pushed = 0;

window.addEventListener("hashchange", () => {
  const r = parse(location.hash);
  if (!r.overlay) pushed = 0;
  route.set(r);
});

export function goTab(tab: Tab): void {
  const cur = route.get();
  // tapping Explore while in it goes back to the top of the section
  const path = tab !== "explore" ? [] : cur.tab === "explore" ? cur.path.slice(0, 1) : explorePath;
  pushed = 0;
  const r = { tab, overlay: null, path };
  history.replaceState(null, "", hashFor(r));
  route.set(r);
}

/** Open a page in Explore. Pages deeper than a section get their own history entry. */
export function goExplore(path: string[], how: "push" | "replace" = path.length > 1 ? "push" : "replace"): void {
  const r: Route = { tab: "explore", overlay: null, path };
  if (how === "push") history.pushState({ nlSub: 1 }, "", hashFor(r));
  else history.replaceState(null, "", hashFor(r));
  route.set(r);
}

/** Back from an Explore page: through history if we came from its parent, else straight up. */
export function exploreBack(): void {
  if ((history.state as { nlSub?: number } | null)?.nlSub) history.back();
  else goExplore(route.get().path.slice(0, -1), "replace");
}

export function openOverlay(overlay: Exclude<Overlay, null>): void {
  const r = { ...route.get(), overlay };
  if (route.get().overlay) {
    // swapping one overlay for another (e.g. name -> name) keeps a single history entry
    history.replaceState(null, "", hashFor(r));
  } else {
    history.pushState(null, "", hashFor(r));
    pushed++;
  }
  route.set(r);
}

export function closeOverlay(): void {
  if (!route.get().overlay) return;
  if (pushed > 0) {
    pushed--;
    history.back();
  } else {
    const r = { ...route.get(), overlay: null };
    history.replaceState(null, "", hashFor(r));
    route.set(r);
  }
}

export function openName(key: string): void {
  openOverlay({ name: key });
}

export function overlayName(o: Overlay): string | null {
  return o && typeof o === "object" ? o.name : null;
}
